import { useState, useEffect, useCallback } from 'react';
import {
  Film, Plus, Pencil, Trash2, Eye, EyeOff, Star,
  Send, Loader2, GripVertical, ExternalLink, Check, CalendarDays, Clock, Play, X
} from 'lucide-react';
import ShortsSection from './ShortsSection';
import VideoModal from '@/components/common/VideoModal';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Brand', 'Technical', "Summer '26"];
const COLORS = ['#dc2626', '#e85d75', '#6366f1', '#f97316', '#10b981', '#3b82f6', '#0066CC', '#14b8a6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4'];

const VideoTab = ({ headers }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingVideo, setEditingVideo] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [distributing, setDistributing] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  // Video preview modal (lets admin watch a video without leaving the page)
  const [previewVideo, setPreviewVideo] = useState(null);

  const emptyForm = {
    video_id: '', title: '', description: '', tagline: '', duration: '0:30',
    category: 'Brand', color: '#0066CC', src: '', featured: false, status: 'published', order: 999,
  };
  const [formData, setFormData] = useState(emptyForm);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- headers is stable (AUTH_HEADERS constant)
  const fetchVideos = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/videos`, { headers, credentials: 'include' });
      const data = await res.json();
      setVideos(data.videos || []);
    } catch (e) {
      console.error('Failed to fetch videos:', e);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const seedVideos = async () => {
    setLoading(true);
    try {
      await fetch(`${API}/api/admin/videos/seed`, { method: 'POST', headers, credentials: 'include' });
      await fetchVideos();
    } catch (e) {
      console.error('Failed to seed videos:', e);
    }
  };

  const handleSave = async () => {
    setSaveStatus('saving');
    try {
      const method = editingVideo ? 'PUT' : 'POST';
      const url = editingVideo
        ? `${API}/api/admin/videos/${editingVideo}`
        : `${API}/api/admin/videos`;
      const res = await fetch(url, {
        method,
        headers: { ...headers, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Save failed');
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
      setShowForm(false);
      setEditingVideo(null);
      setFormData(emptyForm);
      await fetchVideos();
    } catch (e) {
      setSaveStatus('error');
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm(`Delete video "${videoId}"?`)) return;
    try {
      await fetch(`${API}/api/admin/videos/${videoId}`, { method: 'DELETE', headers, credentials: 'include' });
      await fetchVideos();
    } catch (e) {
      console.error('Failed to delete video:', e);
    }
  };

  const toggleStatus = async (video) => {
    const newStatus = video.status === 'published' ? 'draft' : 'published';
    await fetch(`${API}/api/admin/videos/${video.video_id}`, {
      method: 'PUT',
      headers: { ...headers, 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ status: newStatus }),
    });
    await fetchVideos();
  };

  const toggleFeatured = async (video) => {
    await fetch(`${API}/api/admin/videos/${video.video_id}`, {
      method: 'PUT',
      headers: { ...headers, 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ featured: !video.featured }),
    });
    await fetchVideos();
  };

  const handleDistribute = async (videoId, platforms) => {
    setDistributing(videoId);
    try {
      await fetch(`${API}/api/admin/videos/${videoId}/distribute`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ platforms }),
      });
    } catch (e) {
      console.error('Failed to distribute video:', e);
    } finally {
      setTimeout(() => setDistributing(null), 2000);
    }
  };

  const startEdit = (video) => {
    setFormData({ ...video });
    setEditingVideo(video.video_id);
    setShowForm(true);
  };

  const startCreate = () => {
    setFormData(emptyForm);
    setEditingVideo(null);
    setShowForm(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-taxera-blue" />
      </div>
    );
  }

  const published = videos.filter(v => v.status === 'published');
  const drafts = videos.filter(v => v.status === 'draft');

  // Reusable form fields — used in two places:
  //   1) Top "Add New Video" panel when creating a new entry
  //   2) Inline expanded panel beneath a video row when editing that row
  const renderFormFields = () => (
    <>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Video ID (slug)</label>
          <input
            value={formData.video_id}
            onChange={(e) => setFormData({...formData, video_id: e.target.value})}
            disabled={!!editingVideo}
            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm disabled:opacity-50"
            placeholder="my-video-slug"
            data-testid="video-form-id"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Title</label>
          <input
            value={formData.title}
            onChange={(e) => setFormData({...formData, title: e.target.value})}
            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
            placeholder="Video Title"
            data-testid="video-form-title"
          />
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm resize-none"
            rows={2}
            placeholder="Short description..."
            data-testid="video-form-description"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Tagline</label>
          <input
            value={formData.tagline}
            onChange={(e) => setFormData({...formData, tagline: e.target.value})}
            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
            placeholder="Short tagline"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Duration</label>
          <input
            value={formData.duration}
            onChange={(e) => setFormData({...formData, duration: e.target.value})}
            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
            placeholder="0:30"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Source URL (path)</label>
          <input
            value={formData.src}
            onChange={(e) => setFormData({...formData, src: e.target.value})}
            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
            placeholder="/api/media/videos/video-id"
            data-testid="video-form-src"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Category</label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({...formData, category: e.target.value})}
            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
            data-testid="video-form-category"
          >
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Color</label>
          <div className="flex gap-1.5 flex-wrap">
            {COLORS.map(c => (
              <button
                key={c}
                onClick={() => setFormData({...formData, color: c})}
                className={`w-6 h-6 rounded-full border-2 transition-transform ${formData.color === c ? 'border-taxera-charcoal scale-110' : 'border-transparent'}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({...formData, status: e.target.value, ...(e.target.value !== 'scheduled' ? { publish_at: '' } : {})})}
            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
            data-testid="video-form-status"
          >
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
            <option value="published">Published (now)</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        {formData.status === 'scheduled' && (
          <div className="col-span-2">
            <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center gap-1">
              <CalendarDays className="w-3.5 h-3.5" /> Publish Date & Time
            </label>
            <div className="flex items-center gap-3">
              <input
                type="datetime-local"
                value={formData.publish_at ? formData.publish_at.slice(0, 16) : ''}
                onChange={(e) => setFormData({...formData, publish_at: e.target.value ? new Date(e.target.value).toISOString() : ''})}
                className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                data-testid="video-form-publish-at"
              />
              <span className="text-xs text-slate-400">Publishes automatically</span>
            </div>
          </div>
        )}
        <div className="flex items-center gap-2 mt-4">
          <input
            type="checkbox"
            checked={formData.featured}
            onChange={(e) => setFormData({...formData, featured: e.target.checked})}
            id={`featured-check-${editingVideo || 'new'}`}
            className="rounded border-slate-300"
          />
          <label htmlFor={`featured-check-${editingVideo || 'new'}`} className="text-sm text-slate-600">Featured video</label>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={handleSave}
          disabled={saveStatus === 'saving'}
          className="inline-flex items-center gap-2 px-5 py-2 bg-taxera-blue text-white rounded-lg text-sm font-medium hover:bg-taxera-blue/90 disabled:opacity-50 transition-colors"
          data-testid="video-form-save"
        >
          {saveStatus === 'saving' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {saveStatus === 'saved' && <Check className="w-3.5 h-3.5" />}
          {editingVideo ? 'Update Video' : 'Create Video'}
        </button>
        <button
          onClick={() => { setShowForm(false); setEditingVideo(null); }}
          className="px-5 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-200 transition-colors"
        >
          Cancel
        </button>
      </div>
    </>
  );

  return (
    <div className="space-y-6" data-testid="admin-video-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-taxera-charcoal">Video Library CMS</h2>
          <p className="text-sm text-slate-500 mt-1">{videos.length} videos total — {published.length} published, {drafts.length} drafts</p>
        </div>
        <div className="flex gap-2">
          {videos.length === 0 && (
            <button onClick={seedVideos} className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-sm font-medium hover:bg-amber-100 transition-colors" data-testid="seed-videos-btn">
              <Film className="w-4 h-4" /> Seed Default Videos
            </button>
          )}
          <button onClick={startCreate} className="inline-flex items-center gap-2 px-4 py-2 bg-taxera-blue text-white rounded-lg text-sm font-medium hover:bg-taxera-blue/90 transition-colors" data-testid="add-video-btn">
            <Plus className="w-4 h-4" /> Add Video
          </button>
        </div>
      </div>

      {/* Form Panel — only at the top when CREATING a new video.
          When EDITING, the form is rendered inline beneath the row (see below). */}
      {showForm && !editingVideo && (
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="video-form">
          <h3 className="text-lg font-bold text-taxera-charcoal mb-4">
            Add New Video
          </h3>
          {renderFormFields()}
        </div>
      )}
      <div className="space-y-3">
        {videos.map((video, i) => {
          const isEditing = editingVideo === video.video_id;
          return (
          <div key={video.video_id} data-testid={`video-row-wrapper-${video.video_id}`}>
          <div
            className={`flex items-center gap-4 bg-white border p-4 transition-all hover:shadow-sm ${
              isEditing ? 'rounded-t-xl border-b-0' : 'rounded-xl'
            } ${
              video.status === 'draft' ? 'border-amber-200 bg-amber-50/30' :
              video.status === 'scheduled' ? 'border-blue-200 bg-blue-50/30' :
              video.status === 'archived' ? 'border-slate-300 bg-slate-50/50 opacity-60' :
              'border-slate-200'
            }`}
            data-testid={`video-row-${video.video_id}`}
          >
            {/* Order indicator */}
            <div className="flex items-center gap-1 text-slate-300">
              <GripVertical className="w-4 h-4" />
              <span className="text-xs font-mono w-5 text-center">{i + 1}</span>
            </div>

            {/* Color dot + category */}
            <div className="flex items-center gap-2 w-28 shrink-0">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: video.color }} />
              <span className="text-2xs font-bold uppercase tracking-wide text-slate-400">{video.category}</span>
            </div>

            {/* Title + description */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-taxera-charcoal truncate">{video.title}</span>
                {video.featured && (
                  <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500 shrink-0" />
                )}
                {video.status === 'draft' && (
                  <span className="text-2xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-semibold">DRAFT</span>
                )}
                {video.status === 'scheduled' && (
                  <span className="text-2xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-semibold flex items-center gap-1">
                    <Clock className="w-3 h-3" /> SCHEDULED
                  </span>
                )}
                {video.status === 'archived' && (
                  <span className="text-2xs px-2 py-0.5 bg-slate-200 text-slate-500 rounded-full font-semibold">ARCHIVED</span>
                )}
                {video.status === 'published' && (
                  <span className="text-2xs px-2 py-0.5 bg-green-50 text-green-600 rounded-full font-semibold">LIVE</span>
                )}
              </div>
              <p className="text-xs text-slate-400 truncate mt-0.5">{video.description}</p>
              {video.status === 'scheduled' && video.publish_at && (
                <p className="text-2xs text-blue-500 mt-0.5 flex items-center gap-1">
                  <CalendarDays className="w-3 h-3" />
                  Publishes {new Date(video.publish_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
                </p>
              )}
            </div>

            {/* Duration */}
            <span className="text-xs font-mono text-slate-400 w-12 text-center shrink-0">{video.duration}</span>

            {/* Actions */}
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => setPreviewVideo(video)}
                disabled={!video.src}
                className="p-1.5 rounded-lg text-slate-400 hover:text-taxera-blue hover:bg-taxera-blue/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title={video.src ? 'Play preview' : 'No video source'}
                data-testid={`video-preview-${video.video_id}`}
              >
                <Play className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => toggleFeatured(video)}
                className={`p-1.5 rounded-lg transition-colors ${video.featured ? 'text-amber-500 bg-amber-50' : 'text-slate-300 hover:text-amber-500 hover:bg-amber-50'}`}
                title={video.featured ? 'Unfeature' : 'Set as featured'}
                data-testid={`video-feature-${video.video_id}`}
              >
                <Star className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => toggleStatus(video)}
                className={`p-1.5 rounded-lg transition-colors ${video.status === 'published' ? 'text-green-500 bg-green-50' : 'text-slate-300 hover:text-green-500 hover:bg-green-50'}`}
                title={video.status === 'published' ? 'Unpublish (set as draft)' : 'Publish'}
                data-testid={`video-toggle-${video.video_id}`}
              >
                {video.status === 'published' ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={() => isEditing ? (setShowForm(false), setEditingVideo(null)) : startEdit(video)}
                className={`p-1.5 rounded-lg transition-colors ${
                  isEditing ? 'text-taxera-blue bg-taxera-blue/10' : 'text-slate-400 hover:text-taxera-blue hover:bg-taxera-blue/10'
                }`}
                title={isEditing ? 'Close editor' : 'Edit'}
                data-testid={`video-edit-${video.video_id}`}
              >
                {isEditing ? <X className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={() => handleDistribute(video.video_id, ['LinkedIn', 'YouTube'])}
                className={`p-1.5 rounded-lg transition-colors ${
                  distributing === video.video_id ? 'text-green-500 bg-green-50' : 'text-slate-400 hover:text-violet-600 hover:bg-violet-50'
                }`}
                title="Push to Social Media"
                data-testid={`video-distribute-${video.video_id}`}
              >
                {distributing === video.video_id ? <Check className="w-3.5 h-3.5" /> : <Send className="w-3.5 h-3.5" />}
              </button>
              <a
                href={`/resources/videos/${video.video_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 rounded-lg text-slate-400 hover:text-taxera-blue hover:bg-taxera-blue/10 transition-colors"
                title="View on site"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <button
                onClick={() => handleDelete(video.video_id)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                title="Delete"
                data-testid={`video-delete-${video.video_id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Inline expand: edit form opens directly beneath this row */}
          {isEditing && (
            <div className="bg-white border border-t-0 border-slate-200 rounded-b-xl p-6" data-testid={`video-form-inline-${video.video_id}`}>
              <h3 className="text-sm font-bold text-taxera-charcoal mb-4 flex items-center gap-2">
                <Pencil className="w-3.5 h-3.5 text-taxera-blue" /> Editing: {video.title}
              </h3>
              {renderFormFields()}
            </div>
          )}
          </div>
          );
        })}
      </div>

      {videos.length === 0 && (
        <div className="text-center py-16 text-slate-400">
          <Film className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No videos yet. Seed the default library or add one manually.</p>
        </div>
      )}

      {/* ─── Shorts (9:16) Section ─── */}
      <ShortsSection headers={headers} />

      {/* Video preview modal — opens when admin clicks the Play button on a row */}
      <VideoModal
        isOpen={!!previewVideo}
        onClose={() => setPreviewVideo(null)}
        video={previewVideo ? {
          ...previewVideo,
          src: previewVideo.src?.startsWith('/') ? `${API}${previewVideo.src}` : previewVideo.src,
        } : null}
      />
    </div>
  );
};

export default VideoTab;
